import { useState } from 'react';
import type { ScheduleMonth } from '@/lib/calc/engine';
import { formatInr, formatDate } from '@/lib/format';
import { StatusBadge } from '@/components/StatusBadge';
import { ChevronIcon } from '@/components/icons';

interface MonthRowProps {
  month: ScheduleMonth;
}

export function MonthRow({ month }: MonthRowProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card" style={{ marginBottom: 8, padding: 12 }}>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          color: 'var(--color-text)',
        }}
      >
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600 }}>
            {formatDate(month.emiDate)}
            {month.phase === 'pre_emi' && <span className="field-hint"> · interest only</span>}
          </span>
          <span className="row" style={{ gap: 8, alignItems: 'center' }}>
            <StatusBadge status={month.status} />
            <span className="mono">{formatInr(month.emiAmount, true)}</span>
            <ChevronIcon size={16} className={`disclosure-chevron${expanded ? ' is-expanded' : ''}`} />
          </span>
        </div>
      </button>

      {expanded && (
        <div className="stack" style={{ marginTop: 12, gap: 7 }}>
          <DetailRow label="Opening Balance" value={formatInr(month.openingBalance, true)} />
          {month.disbursedInCycle > 0 && <DetailRow label="Disbursed this cycle" value={formatInr(month.disbursedInCycle, true)} />}
          <DetailRow label="Interest Component" value={formatInr(month.interestComponent, true)} />
          <DetailRow label="Principal Component" value={formatInr(month.principalComponent, true)} />
          {month.prepaymentAmount > 0 && (
            <DetailRow label="Prepayment" value={formatInr(month.prepaymentAmount, true)} />
          )}
          <DetailRow label="Closing Balance" value={formatInr(month.closingBalance, true)} />
          <DetailRow label="Cumulative Interest" value={formatInr(month.cumulativeInterest, true)} />
          <DetailRow label="Cumulative Principal" value={formatInr(month.cumulativePrincipal, true)} />
          <DetailRow label="Cumulative Total Payment" value={formatInr(month.cumulativeTotalPayment, true)} />
          {month.isReconciled && (
            <p className="field-hint" style={{ marginTop: 4 }}>
              Reconciled with an LIC-confirmed balance correction effective this cycle.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between' }}>
      <span className="text-muted">{label}</span>
      <span className="mono">{value}</span>
    </div>
  );
}
